import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createMasterDataService } from "../../services/masterDataService";
import { Plus, Edit2, Trash2, CheckCircle, AlertTriangle } from "lucide-react";
import useAuth from "../../contexts/AuthContext";
import { UserRole } from "../../types/status";
import DocumentUpload from "../../common_components/DocumentUpload";

type TabId = "departments" | "designations" | "instructions" | "bankAccount";

interface Tab {
  id: TabId;
  label: string;
}

interface MasterItem {
  _id: string;
  name?: string;
  text?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  category?: string;
  module?: string;
  order?: number;
  icon?: string;
}

interface BankAccount {
  bankName?: string;
  branchName?: string;
  accountNumber?: string;
  ifsc?: string;
  qrCodeUrl?: string;
  qrCodeKey?: string;
}

interface DataState {
  departments: MasterItem[];
  designations: MasterItem[];
  instructions: MasterItem[];
  bankAccount: BankAccount | null;
}

const MasterDataManagement: React.FC = () => {
  const { apiRequest, isRecruitmentAdmin } = useAuth();
  const masterDataService = useMemo(() => createMasterDataService({ apiRequest }), [apiRequest]);
  const [activeTab, setActiveTab] = useState<TabId>("departments");
  const [data, setData] = useState<DataState>({
    departments: [],
    designations: [],
    instructions: [],
    bankAccount: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const options = { all: true, showDeleted };
      const [depRes, desRes, instRes] = await Promise.all([
        masterDataService.getDepartments(options),
        masterDataService.getDesignations(undefined, options),
        masterDataService.getInstructions(undefined, options),
      ]);

      let bankAcc: BankAccount | null = null;
      try {
        bankAcc = await masterDataService.getBankAccount();
      } catch (err: unknown) {
        const e = err as { status?: number };
        if (e.status !== 404) {
          console.error("Error loading bank account:", err);
        }
      }

      setData({
        departments: depRes.data || [],
        designations: desRes.data || [],
        instructions: instRes.data || [],
        bankAccount: bankAcc,
      });
      setError(null);
    } catch (err) {
      setError("Failed to fetch master data.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [masterDataService, showDeleted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showMessage = (msg: string, isError = false) => {
    if (isError) {
      setError(msg);
      setTimeout(() => setError(null), 3000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleSeed = async () => {
    showMessage("Initial data migration is disabled as constants are removed.", true);
  };

  const handleEdit = (item: MasterItem) => {
    setIsEditing(true);
    setCurrentId(item._id);
    setFormData(item as unknown as Record<string, unknown>);
  };

  const handleDelete = async (id: string, type: TabId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      if (type === "departments") await masterDataService.deleteDepartment(id);
      if (type === "designations") await masterDataService.deleteDesignation(id);
      if (type === "instructions") await masterDataService.deleteInstruction(id);

      showMessage("Item deleted successfully!");
      fetchData();
    } catch (err) {
      showMessage("Failed to delete item.", true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (activeTab === "departments") {
        if (isEditing) await masterDataService.updateDepartment(currentId!, formData);
        else await masterDataService.createDepartment(formData);
      } else if (activeTab === "designations") {
        if (isEditing) await masterDataService.updateDesignation(currentId!, formData);
        else await masterDataService.createDesignation(formData);
      } else if (activeTab === "instructions") {
        if (isEditing) await masterDataService.updateInstruction(currentId!, formData);
        else await masterDataService.createInstruction(formData);
      } else if (activeTab === "bankAccount") {
        await masterDataService.saveBankAccount(formData);
      }

      showMessage(
        activeTab === "bankAccount"
          ? "Bank Account details saved successfully!"
          : `Item ${isEditing ? "updated" : "created"} successfully!`
      );
      if (activeTab === "bankAccount") {
        const updated = await masterDataService.getBankAccount().catch(() => null);
        setData((prev) => ({ ...prev, bankAccount: updated }));
        setFormData((updated as Record<string, unknown>) || {});
      } else {
        resetForm();
        fetchData();
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      showMessage(`Failed to save: ${e.message}`, true);
    }
  };

  const resetForm = (targetTab: TabId = activeTab) => {
    setIsEditing(targetTab === "bankAccount");
    setCurrentId(null);
    setFormData(targetTab === "bankAccount" ? (data.bankAccount as Record<string, unknown>) || {} : {});
  };

  const tabs: Tab[] = [
    { id: "departments", label: "Departments" },
    { id: "designations", label: "Posts (Designations)" },
    { id: "instructions", label: "Initial Instructions" },
    { id: "bankAccount", label: "Bank Account Details" },
  ];

  const renderForm = () => {
    return (
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-8 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {isEditing ? "Edit" : "Add New"} {tabs.find((t) => t.id === activeTab)?.label}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(activeTab === "departments" || activeTab === "designations") && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={(formData.name as string) || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
          )}

          {activeTab === "designations" && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={(formData.category as string) || "Both"}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="Both">Both</option>
                <option value="Faculty">Faculty</option>
                <option value="Non-Faculty">Non-Faculty</option>
              </select>
            </div>
          )}

          {activeTab === "instructions" && (
            <>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Instruction Text</label>
                <textarea
                  required
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={(formData.text as string) || ""}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Module</label>
                <select
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={(formData.module as string) || "All"}
                  onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                >
                  <option value="All">All Modules</option>
                  <option value="Faculty Recruitment">Faculty Recruitment</option>
                  <option value="Non-Faculty Recruitment">Non-Faculty Recruitment</option>
                  <option value="Student Admission">Student Admission</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Icon Name (Lucide)</label>
                <input
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={(formData.icon as string) || "CheckSquare"}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Color Class (Tailwind)</label>
                <input
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={(formData.color as string) || "text-green-600"}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Order</label>
                <input
                  type="number"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={(formData.order as number) || 0}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>
            </>
          )}

          {activeTab === "bankAccount" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Bank Name *</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={(formData.bankName as string) || ""}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Branch Name *</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={(formData.branchName as string) || ""}
                  onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Account Number *</label>
                <input
                  type="text"
                  required
                  pattern="[0-9]{9,18}"
                  title="9 to 18 digits number"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={(formData.accountNumber as string) || ""}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">IFSC Code *</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm uppercase"
                  value={(formData.ifsc as string) || ""}
                  onChange={(e) => setFormData({ ...formData, ifsc: e.target.value })}
                />
              </div>
              <div className="md:col-span-2">
                <DocumentUpload
                  applicationId="bank-master"
                  docType="qrCode"
                  label="Upload Bank QR Code Image"
                  folder="master"
                  existingDoc={
                    formData.qrCodeKey
                      ? { key: formData.qrCodeKey as string, url: (formData.qrCodeUrl as string) || undefined }
                      : null
                  }
                  onUploadComplete={(_, docData) => {
                    setFormData((prev) => ({
                      ...prev,
                      qrCodeKey: docData?.key || "",
                      qrCodeUrl: docData?.url || "",
                    }));
                  }}
                />
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          {isEditing && (
            <button
              type="button"
              onClick={() => resetForm()}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded border border-slate-700 shadow-sm font-medium hover:bg-slate-900 transition-all"
          >
            {isEditing ? "Update" : "Save"}
          </button>
        </div>
      </form>
    );
  };

  const renderTable = () => {
    if (activeTab === "bankAccount") {
      const acc = data.bankAccount;
      if (!acc) {
        return (
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 text-center text-gray-500 text-sm">
            No bank account details saved yet. Please fill the form above to initialize.
          </div>
        );
      }
      return (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 space-y-4 max-w-2xl">
          <h3 className="text-lg font-bold text-gray-900 border-b pb-2">Current Active Bank Account</h3>
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <span className="block font-semibold text-gray-500">Bank Name</span>
              <span className="font-medium">{acc.bankName}</span>
            </div>
            <div>
              <span className="block font-semibold text-gray-500">Branch Name</span>
              <span className="font-medium">{acc.branchName}</span>
            </div>
            <div>
              <span className="block font-semibold text-gray-500">Account Number</span>
              <span className="font-medium font-mono">{acc.accountNumber}</span>
            </div>
            <div>
              <span className="block font-semibold text-gray-500">IFSC Code</span>
              <span className="font-medium font-mono uppercase">{acc.ifsc}</span>
            </div>
            {acc.qrCodeUrl && (
              <div className="col-span-2 pt-2">
                <span className="block font-semibold text-gray-500 mb-2">QR Code Image</span>
                <img
                  src={acc.qrCodeUrl}
                  alt="QR Code"
                  className="w-48 h-48 object-contain border border-gray-200 p-2 rounded-lg bg-gray-50"
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    if (img.src !== acc.qrCodeKey) {
                      img.src = acc.qrCodeKey || "";
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    const currentData: MasterItem[] = data[activeTab] || [];

    return (
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {activeTab === "instructions" ? "Text" : "Name"}
                    </th>
                    {activeTab === "designations" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                    )}
                    {activeTab === "instructions" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order / Details
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                        No data found.
                      </td>
                    </tr>
                  ) : (
                    currentData.map((item) => (
                      <tr key={item._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-md truncate">
                          {item.name || item.text}
                        </td>
                        {activeTab === "designations" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.category}
                          </td>
                        )}
                        {activeTab === "instructions" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Module: {item.module || "All"} | Order: {item.order} | Icon: {item.icon}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              item.isDeleted
                                ? "bg-red-100 text-red-800 border border-red-200"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {item.isDeleted ? "DELETED" : "Active"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {item.isDeleted ? (
                            <span className="text-xs font-bold text-red-700 bg-red-100 px-2 py-1 rounded border border-red-200">
                              DELETED
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(item)}
                                className="text-indigo-600 hover:text-indigo-900 mr-4"
                              >
                                <Edit2 className="w-4 h-4 inline" />
                              </button>
                              <button
                                onClick={() => handleDelete(item._id, activeTab)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="w-4 h-4 inline" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Master Data Management</h1>
          <p className="mt-1 text-sm text-gray-500">Manage drop-downs and dynamic lists across the platform.</p>
        </div>
        <div className="flex items-center gap-3">
          {isRecruitmentAdmin && (
            <label className="inline-flex items-center cursor-pointer gap-2 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-md border border-red-200 transition-colors shadow-sm">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="rounded text-red-600 focus:ring-red-500 border-red-300 w-4 h-4 cursor-pointer"
              />
              <span className="text-xs font-semibold text-red-700">Show Deleted Items</span>
            </label>
          )}
          <button
            onClick={handleSeed}
            className="bg-indigo-50 border border-indigo-200 text-indigo-700 px-4 py-2 rounded-md hover:bg-indigo-100 transition-colors flex items-center shadow-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Migrate Initial Data
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 p-4 rounded-md flex items-center text-red-700">
          <AlertTriangle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 p-4 rounded-md flex items-center text-green-700">
          <CheckCircle className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-8 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                resetForm(tab.id);
              }}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {renderForm()}
          {renderTable()}
        </>
      )}
    </div>
  );
};

export default MasterDataManagement;
